import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { superAdminGuard } from './core/guards/super-admin.guard';
import { syncInitGuard } from './core/guards/sync-init.guard';
import { organizationResolver } from './core/resolvers/organization.resolver';
import { userResolver } from './core/resolvers/user.resolver';
import { AcceptInvitationComponent } from './pages/accept-invitation/accept-invitation.component';
import { ActionSheetComponent, ActionSheetConfig } from './pages/map/action-sheet/action-sheet.component';
import { AddressDetailComponent } from './pages/address-detail/address-detail.component';
import { AddressFormComponent } from './pages/address-form/address-form.component';
import { AddressesComponent } from './pages/addresses/addresses.component';
import { AdminRegionsComponent } from './pages/admin/regions/admin-regions.component';
import { AdminSubscriptionsComponent } from './pages/admin/subscriptions/admin-subscriptions.component';
import { AuthCallbackComponent } from './pages/auth-callback/auth-callback.component';
import { BillingSuspendedComponent } from './pages/billing-suspended/billing-suspended.component';
import { BrowserErrorComponent } from './pages/browser-error/browser-error.component';
import { DbTestComponent } from './pages/db-test/db-test.component';
import { DevSqlComponent } from './pages/dev-sql/dev-sql.component';
import { DiagnosticsComponent } from './pages/diagnostics/diagnostics.component';
import { ErrorComponent } from './pages/error/error.component';
import { ForbiddenComponent } from './pages/forbidden/forbidden.component';
import { HomeComponent } from './pages/home/home.component';
import { LandingComponent } from './pages/landing/landing.component';
import { LeaderboardComponent } from './pages/leaderboard/leaderboard.component';
import { LeadWizardComponent } from './pages/lead-wizard/lead-wizard.component';
import { LeadDetailComponent } from './pages/leads/lead-detail/lead-detail.component';
import { LeadsListComponent } from './pages/leads/leads-list/leads-list.component';
import { PrivacyPolicyComponent } from './pages/legal/privacy-policy.component';
import { AppointmentScheduleComponent } from './pages/reports/appointment-schedule/appointment-schedule.component';
import { DailyActivityComponent } from './pages/reports/daily-activity/daily-activity.component';
import { FunnelComponent } from './pages/reports/funnel/funnel.component';
import { LeadExportComponent } from './pages/reports/lead-export/lead-export.component';
import { LeadSummaryComponent } from './pages/reports/lead-summary/lead-summary.component';
import { ObjectionBreakdownComponent } from './pages/reports/objection-breakdown/objection-breakdown.component';
import { RateTrendsComponent } from './pages/reports/rate-trends/rate-trends.component';
import { ReportsHomeComponent } from './pages/reports/reports-home/reports-home.component';
import { TermsOfServiceComponent } from './pages/legal/terms-of-service.component';
import { LoggingComponent } from './pages/logging/logging.component';
import { LoginComponent } from './pages/login/login.component';
import { MapComponent } from './pages/map/map.component';
import { InteractionComponent } from './pages/map/interaction/interaction.component';
import { MyDayComponent } from './pages/my-day/my-day.component';
import { OnboardingComponent } from './pages/onboarding/onboarding.component';
import { OrganizationsComponent } from './pages/organizations/organizations.component';
import { PricingComponent } from './pages/pricing/pricing.component';
import { SettingsAdminComponent } from './pages/settings/admin/settings-admin.component';
import { BillingSettingsComponent } from './pages/settings/billing/billing-settings.component';
import { ConnectionSettingsComponent } from './pages/settings/connection/connection-settings.component';
import { GeneralSettingsComponent } from './pages/settings/general/general-settings.component';
import { InvitationsListComponent } from './pages/settings/invitations/invitations-list.component';
import { OrganizationSettingsComponent } from './pages/settings/organization/organization-settings.component';
import { SettingsHomeComponent } from './pages/settings/settings-home/settings-home.component';
import { SettingsComponent } from './pages/settings/settings.component';
import { ServicesSettingsComponent } from './pages/settings/services/services-settings.component';
import { TagsSettingsComponent } from './pages/settings/tags/tags-settings.component';
import { TeamsSettingsComponent } from './pages/settings/teams/teams-settings.component';
import { WorkContextsSettingsComponent } from './pages/settings/work-contexts/work-contexts-settings.component';
import { SignupCompleteComponent } from './pages/signup-complete/signup-complete.component';
import { SignupComponent } from './pages/signup/signup.component';
import { NetworkActivityComponent } from './pages/network-activity/network-activity.component';
import { SyncErrorComponent } from './pages/sync-error/sync-error.component';

// Per-screen sizing for the shared ActionSheetComponent shell that every /map/addresses/**
// leaf route below renders into (see action-sheet.component.ts) -- desktop/tablet width, and
// whether the mobile sheet opens already at full height or at the default medium 'peek'
// height (draggable up to full from there, same as every other sheet).
const ADDRESSES_LIST_SHEET: ActionSheetConfig = { width: '32rem', startFullHeight: true };
const ADDRESS_FORM_SHEET: ActionSheetConfig = { width: '28rem', startFullHeight: true };
const ADDRESS_DETAIL_SHEET: ActionSheetConfig = { width: '28rem', startFullHeight: true };
const INTERACTION_SHEET: ActionSheetConfig = { width: '24rem', startFullHeight: false };
const LEAD_WIZARD_SHEET: ActionSheetConfig = { width: '40rem', startFullHeight: true };

export const routes: Routes = [
  // Public routes (no auth required)
  { path: 'landing', component: LandingComponent },
  { path: 'pricing', component: PricingComponent },
  { path: 'login', component: LoginComponent },
  { path: 'signup', component: SignupComponent },
  { path: 'oauth-callback', component: AuthCallbackComponent },
  { path: 'browser-error', component: BrowserErrorComponent },
  { path: 'accept-invitation', component: AcceptInvitationComponent },
  { path: 'sync-error', component: SyncErrorComponent },
  { path: 'error', component: ErrorComponent },
  { path: 'terms', component: TermsOfServiceComponent },
  { path: 'privacy', component: PrivacyPolicyComponent },

  // Diagnostic routes (no auth required - for debugging database issues)
  { path: 'db-test', component: DbTestComponent },

  // Onboarding (auth guard + user resolver - user is completing signup with JWT but no org yet)
  {
    path: 'onboarding',
    component: OnboardingComponent,
    canActivate: [authGuard, syncInitGuard],
    resolve: { user: userResolver },
  },

  // Billing lockout (auth guard only - no syncInitGuard/resolvers, since a
  // suspended org's WS connection and most REST calls are themselves blocked)
  {
    path: 'billing-suspended',
    component: BillingSuspendedComponent,
    canActivate: [authGuard],
  },

  // Stripe Checkout success_url/cancel_url target from the signup wizard's
  // paid-plan handoff (see signup.component.ts) - org already exists by now
  {
    path: 'signup-complete',
    component: SignupCompleteComponent,
    canActivate: [authGuard],
  },

  // Organization selection (no auth guard - user has valid JWT but needs to select/switch org)
  {
    path: 'organizations',
    component: OrganizationsComponent,
    data: { breadcrumb: { label: 'org.list.title', parent: '/' } },
  },

  // Protected routes (syncInitGuard ensures sync is initialized before accessing data)
  // User and organization data loaded via resolvers before route activation
  {
    path: 'forbidden',
    component: ForbiddenComponent,
    canActivate: [authGuard, syncInitGuard],
    resolve: { user: userResolver, org: organizationResolver },
  },
  // Default route is the map (see app.html's logo/nav "Map" item) — the
  // dedicated home dashboard lives at /home instead.
  { path: '', redirectTo: 'map', pathMatch: 'full' },
  {
    path: 'home',
    component: HomeComponent,
    canActivate: [authGuard, syncInitGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'nav.home', parent: '/' } },
  },

  // Canvassing — addresses, interactions, and the map. Everything address- and
  // interaction-shaped nests under /map as docked panels over the live map,
  // matching the screen hierarchy in
  // docs/implementation/canvassing-crm-high-level-design.md §12 (Map → Address
  // sheet → Interaction/Detail) — see the "Canvas Route Map" review for the
  // full rationale. MapComponent renders the outer <router-outlet>; every
  // /map/addresses/** leaf below renders into ActionSheetComponent's nested
  // one instead, which owns the shared docked-panel chrome (backdrop,
  // position, mobile peek/full-height, click-outside-to-close) so individual
  // leaf components don't each reimplement it -- see action-sheet.component.ts.
  // Every other node is still a pathless, component-less grouping route that
  // exists only to carry a path segment, param, and guard through to an outlet.
  {
    path: 'map',
    component: MapComponent,
    canActivate: [authGuard, syncInitGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'home.mapButton', parent: null } },
    children: [
      {
        // Plural throughout, list and member alike (matches 'leads'/'leads/:id'
        // below and general REST/URL convention) -- the resource type doesn't
        // change just because the URL narrows to one member.
        path: 'addresses',
        component: ActionSheetComponent,
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: AddressesComponent,
            canActivate: [permissionGuard(['address.read'])],
            data: { breadcrumb: { label: 'nav.addresses', parent: '/map' }, actionSheet: ADDRESSES_LIST_SHEET },
          },
          {
            path: 'new',
            component: AddressFormComponent,
            canActivate: [permissionGuard(['address.create'])],
            data: { breadcrumb: { label: 'addresses.form.newTitle', parent: '/map/addresses' }, actionSheet: ADDRESS_FORM_SHEET },
          },
          {
            // 'new' MUST precede ':id' — ':id' is a param segment and would
            // otherwise greedily match the literal 'new' too; Angular matches
            // sibling routes in array order, first match wins.
            path: ':id',
            children: [
              {
                path: '',
                pathMatch: 'full',
                component: AddressDetailComponent,
                canActivate: [permissionGuard(['address.read'])],
                data: { breadcrumb: { label: ':dynamic', parent: '/map/addresses' }, actionSheet: ADDRESS_DETAIL_SHEET },
              },
              {
                path: 'edit',
                component: AddressFormComponent,
                canActivate: [permissionGuard(['address.update'])],
                data: { breadcrumb: { label: 'addresses.form.editTitle', parent: '/map/addresses/:id' }, actionSheet: ADDRESS_FORM_SHEET },
              },
              {
                path: 'interactions',
                children: [
                  // Same ordering rule as above: 'new' before ':interactionId'.
                  {
                    path: 'new',
                    canActivate: [permissionGuard(['interaction.create'])],
                    children: [
                      {
                        path: '',
                        pathMatch: 'full',
                        component: InteractionComponent,
                        data: { breadcrumb: { label: 'interactions.form.newTitle', parent: '/map/addresses/:id' }, actionSheet: INTERACTION_SHEET },
                      },
                      { path: 'objection', component: InteractionComponent, data: { actionSheet: INTERACTION_SHEET } },
                      { path: 'lead', component: LeadWizardComponent, data: { actionSheet: LEAD_WIZARD_SHEET } },
                    ],
                  },
                  {
                    path: ':interactionId',
                    canActivate: [permissionGuard(['interaction.update'])],
                    children: [
                      {
                        path: '',
                        pathMatch: 'full',
                        component: InteractionComponent,
                        data: { breadcrumb: { label: 'interactions.form.editTitle', parent: '/map/addresses/:id' }, actionSheet: INTERACTION_SHEET },
                      },
                      { path: 'objection', component: InteractionComponent, data: { actionSheet: INTERACTION_SHEET } },
                      { path: 'lead', component: LeadWizardComponent, data: { actionSheet: LEAD_WIZARD_SHEET } },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'my-day',
    component: MyDayComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['interaction.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'myDay.title', parent: '/' } },
  },
  {
    path: 'leads',
    component: LeadsListComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['lead.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'leads.list.title', parent: '/' } },
  },
  {
    path: 'leads/:id',
    component: LeadDetailComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['lead.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: ':dynamic', parent: '/leads' } },
  },
  {
    path: 'reports',
    component: ReportsHomeComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['report.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'reports.home.title', parent: '/' } },
  },
  {
    path: 'reports/daily-activity',
    component: DailyActivityComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['report.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'reports.dailyActivity.title', parent: '/reports' } },
  },
  {
    path: 'reports/rate-trends',
    component: RateTrendsComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['report.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'reports.rateTrends.title', parent: '/reports' } },
  },
  {
    path: 'reports/lead-summary',
    component: LeadSummaryComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['report.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'reports.leadSummary.title', parent: '/reports' } },
  },
  {
    path: 'reports/objection-breakdown',
    component: ObjectionBreakdownComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['report.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'reports.objectionBreakdown.title', parent: '/reports' } },
  },
  {
    path: 'reports/funnel',
    component: FunnelComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['report.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'reports.funnel.title', parent: '/reports' } },
  },
  {
    path: 'reports/appointment-schedule',
    component: AppointmentScheduleComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['report.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'reports.appointmentSchedule.title', parent: '/reports' } },
  },
  {
    path: 'reports/lead-export',
    component: LeadExportComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['report.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'reports.leadExport.title', parent: '/reports' } },
  },
  {
    path: 'leaderboard',
    component: LeaderboardComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['report.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'leaderboard.title', parent: '/' } },
  },

  // Super-admin-only QA tool (cross-org subscription management)
  {
    path: 'admin/subscriptions',
    component: AdminSubscriptionsComponent,
    canActivate: [authGuard, syncInitGuard, superAdminGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'settings.admin.subscriptions', parent: '/' } },
  },

  // Super-admin-only tool: trigger/track address registry region builds
  {
    path: 'admin/regions',
    component: AdminRegionsComponent,
    canActivate: [authGuard, syncInitGuard, superAdminGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'settings.admin.regions', parent: '/' } },
  },

  // Settings - require org.settings; nested under one shell (SettingsComponent) so the
  // section pages render into its <router-outlet> with a slide transition instead of
  // replacing the whole screen -- see settings.component.ts / settings-home.component.ts.
  {
    path: 'settings/network-activity',
    component: NetworkActivityComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['org.settings'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'dev.network.title', parent: '/settings' } },
  },
  {
    path: 'settings/logging',
    component: LoggingComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['org.settings'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'settings.logging.title', parent: '/settings' } },
  },
  // Organization is reachable outside the org.settings-gated settings shell below:
  // every member can view/switch/create organizations here (rename is further
  // gated inside OrganizationSettingsComponent to owners only), whereas the rest
  // of Settings requires org.settings. See app.html's nav item, shown only to
  // users without org.settings (who see it via Settings > Organization instead).
  {
    path: 'settings/organization',
    component: OrganizationSettingsComponent,
    canActivate: [authGuard, syncInitGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'settings.organization.title', parent: '/settings' } },
  },
  {
    path: 'settings',
    component: SettingsComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['org.settings'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'settings.title', parent: '/' } },
    children: [
      { path: '', component: SettingsHomeComponent },
      {
        path: 'members',
        component: InvitationsListComponent,
        canActivate: [permissionGuard(['user.invite'])],
        data: { breadcrumb: { label: 'settings.members.title', parent: '/settings' } },
      },
      {
        path: 'billing',
        component: BillingSettingsComponent,
        canActivate: [permissionGuard(['org.billing'])],
        data: { breadcrumb: { label: 'settings.billing.title', parent: '/settings' } },
      },
      {
        path: 'tags',
        component: TagsSettingsComponent,
        canActivate: [permissionGuard(['config.manage-lists'])],
        data: { breadcrumb: { label: 'settings.tags.title', parent: '/settings' } },
      },
      {
        path: 'services',
        component: ServicesSettingsComponent,
        canActivate: [permissionGuard(['config.manage-lists'])],
        data: { breadcrumb: { label: 'settings.services.title', parent: '/settings' } },
      },
      {
        path: 'teams',
        component: TeamsSettingsComponent,
        canActivate: [permissionGuard(['team.manage'])],
        data: { breadcrumb: { label: 'settings.teams.title', parent: '/settings' } },
      },
      {
        path: 'work-contexts',
        component: WorkContextsSettingsComponent,
        canActivate: [permissionGuard(['work-context.manage'])],
        data: { breadcrumb: { label: 'settings.workContexts.title', parent: '/settings' } },
      },
      {
        path: 'connection',
        component: ConnectionSettingsComponent,
        data: { breadcrumb: { label: 'settings.connection.title', parent: '/settings' } },
      },
      {
        path: 'general',
        component: GeneralSettingsComponent,
        data: { breadcrumb: { label: 'settings.general.title', parent: '/settings' } },
      },
      {
        path: 'admin',
        component: SettingsAdminComponent,
        canActivate: [superAdminGuard],
        data: { breadcrumb: { label: 'settings.admin.title', parent: '/settings' } },
      },
    ],
  },

  // Diagnostics - requires diagnostics.view permission
  {
    path: 'diagnostics',
    component: DiagnosticsComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['diagnostics.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'nav.diagnostics', parent: '/' } },
  },

  // SQL Console - requires sql.view permission (org-owner only)
  {
    path: 'dev/sql',
    component: DevSqlComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['sql.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'nav.devSqlConsole', parent: '/' } },
  },

  { path: '**', redirectTo: '' },
];
